import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { useWorkflowRunner } from "@/workflows/runnerContext";
import { Award, Printer, CheckCircle2, Loader2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";
import { supabase } from "@/integrations/supabase/client";
import {
  createPassport,
  usePassportForWorkOrder,
} from "@/hooks/useProductPassport";

interface WorkflowFinishActionsProps {
  applianceId: string;
  workOrderId: string | null;
  workflowType: "repair" | "refurbish";
  brand?: string | null;
  modelNumber?: string | null;
  category?: string | null;
  applianceName?: string | null;
  qualityGrade?: string | null;
  summaryData?: Record<string, any>;
  /** Workflow item ids of all completed steps (used to resolve spare parts used). */
  completedWorkflowItemIds?: string[];
  /** When true, the finish button is enabled (all steps done). */
  allStepsComplete: boolean;
}

export function WorkflowFinishActions({
  applianceId,
  workOrderId,
  workflowType,
  brand,
  modelNumber,
  category,
  applianceName,
  qualityGrade,
  summaryData,
  completedWorkflowItemIds,
  allStepsComplete,
}: WorkflowFinishActionsProps) {
  const navigate = useNavigate();
  const runner = useWorkflowRunner();
  const { passport, refresh } = usePassportForWorkOrder(workOrderId);
  const [creating, setCreating] = useState(false);

  const handleFinish = async () => {
    if (!applianceId) return;
    setCreating(true);
    try {
      // Resolve spare parts used (from workflow item bindings → catalog).
      // Catalog entries are filtered by brand and model_pattern so the passport
      // only lists parts that actually fit the current appliance.
      let partsUsed: Array<{ name: string; part_number: string | null; quantity: number; price: number; currency: string }> = [];
      const itemIds = (completedWorkflowItemIds || []).filter(Boolean);
      if (itemIds.length > 0) {
        const { data: bindings } = await supabase
          .from("spare_part_workflow_bindings" as any)
          .select("catalog_id, quantity, workflow_item_id")
          .in("workflow_item_id", itemIds);
        const rows = (bindings || []) as unknown as Array<{ catalog_id: string; quantity: number }>;
        if (rows.length > 0) {
          const catIds = [...new Set(rows.map((r) => r.catalog_id))];
          const { data: cat } = await supabase
            .from("spare_parts_catalog" as any)
            .select("id, name, part_number, price, currency, brand, model_pattern")
            .in("id", catIds);

          const normBrand = (brand || "").trim().toLowerCase();
          const normModel = (modelNumber || "").trim().toLowerCase();
          const fits = (c: any) => {
            if (c.brand && normBrand && String(c.brand).trim().toLowerCase() !== normBrand) {
              return false;
            }
            if (c.model_pattern && normModel) {
              // model_pattern may list multiple tokens separated by spaces/commas
              const tokens = String(c.model_pattern)
                .split(/[\s,;]+/)
                .map((t) => t.trim().toLowerCase())
                .filter(Boolean);
              if (tokens.length > 0 && !tokens.some((t) => normModel.includes(t))) {
                return false;
              }
            }
            return true;
          };

          const catMap = new Map(((cat || []) as any[]).filter(fits).map((c) => [c.id, c]));
          const agg = new Map<string, { name: string; part_number: string | null; quantity: number; price: number; currency: string }>();
          for (const r of rows) {
            const c = catMap.get(r.catalog_id);
            if (!c) continue;
            const existing = agg.get(r.catalog_id);
            const qty = Number(r.quantity || 1);
            if (existing) {
              existing.quantity += qty;
            } else {
              agg.set(r.catalog_id, {
                name: c.name,
                part_number: c.part_number ?? null,
                quantity: qty,
                price: Number(c.price || 0),
                currency: c.currency || "EUR",
              });
            }
          }
          partsUsed = Array.from(agg.values());
        }
      }

      // Resolve refurbishing company
      let companyName: string | null = null;
      try {
        const { data: sessionData } = await supabase.auth.getSession();
        const userId = sessionData?.session?.user?.id;
        if (userId) {
          const { data: uc } = await supabase
            .from("user_companies")
            .select("company_id")
            .eq("user_id", userId)
            .maybeSingle();
          if (uc?.company_id) {
            const { data: comp } = await supabase
              .from("companies")
              .select("name")
              .eq("id", uc.company_id)
              .maybeSingle();
            companyName = comp?.name ?? null;
          }
        }
      } catch (e) {
        console.warn("Failed to resolve company for passport", e);
      }

      const enrichedSummary = {
        ...(summaryData || {}),
        partsUsed,
        company: companyName,
      };

      const created = await createPassport({
        applianceId,
        workOrderId,
        workflowType,
        brand,
        modelNumber,
        category,
        applianceName,
        qualityGrade,
        summaryData: enrichedSummary,
      });
      // Mark work order as completed
      if (workOrderId) {
        await supabase
          .from("work_orders")
          .update({ status: "completed", completed_at: new Date().toISOString() })
          .eq("id", workOrderId);
      }
      await refresh();
      toast.success(`Passport generated · Serial ${created.serial_number}`);
      if (runner) runner.complete({ passportId: created.id });
      else navigate(`/passport/${created.id}`);
    } catch (err: any) {
      console.error("Failed to create passport", err);
      toast.error(err?.message || "Failed to generate passport");
    } finally {
      setCreating(false);
    }
  };


  if (passport) {
    return (
      <div className="bg-card rounded-2xl border-2 border-refurbish/30 p-4">
        <div className="flex items-center gap-2 mb-3">
          <CheckCircle2 className="w-5 h-5 text-refurbish" />
          <p className="font-semibold text-foreground">
            Workflow Complete · Serial {passport.serial_number}
          </p>
        </div>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
          <Button
            onClick={() => navigate(`/passport/${passport.id}`)}
            className="gap-2 bg-refurbish hover:bg-refurbish/90"
          >
            <Award className="w-4 h-4" />
            View Digital Product Passport
          </Button>
          <Button
            variant="outline"
            onClick={() => window.open(`/passport/${passport.id}/label`, "_blank")}
            className="gap-2"
          >
            <Printer className="w-4 h-4" />
            Print QR Label
          </Button>
        </div>
      </div>
    );
  }

  if (!allStepsComplete) return null;

  return (
    <div className="bg-card rounded-2xl border-2 border-primary/30 p-4">
      <Button
        onClick={handleFinish}
        disabled={creating}
        className="w-full gap-2 bg-refurbish hover:bg-refurbish/90"
        size="lg"
      >
        {creating ? (
          <>
            <Loader2 className="w-4 h-4 animate-spin" />
            Generating Passport…
          </>
        ) : (
          <>
            <Award className="w-4 h-4" />
            Finish & Generate Digital Product Passport
          </>
        )}
      </Button>
    </div>
  );
}
